/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PrimaryButton } from '@fluentui/react/lib/Button';
import { ComboBox, IComboBoxProps, IComboBoxStyles } from '@fluentui/react/lib/ComboBox';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import debounce from 'lodash.debounce';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ReviewEndpointUtterancesUIStore } from 'src/luis/appShell/buildPane/reviewEndpointPage/ReviewEndpointUtterancesUIStore';
import { SuggestedUtteranceRow } from 'src/luis/projects/utterances/components/SuggestUtterancesTable/SuggestedUtteranceRow';
import { SuggestedUtteranceObservable } from 'src/luis/projects/utterances/utils/generateSuggestedUtterancesObservables';
import { LuisId } from 'src/luis/types/Resource';
import { ResizeProvider, useResizeTrigger } from 'src/modules/labeling/contexts/ResizeContext';

const comboBoxStyles = classNamesFunction<IComboBoxProps, IComboBoxStyles>()({
    container: { width: '100%' },
    optionsContainerWrapper: { maxHeight: 300 }
});

export type SuggestUtterancesTableProps = TableProps<SuggestedUtteranceObservable> & {
    computedSuggestedUtterances: SuggestedUtteranceObservable[];
    isLoading: boolean;
    isRTL: boolean;
    reviewEndpointUtteranceUIStore: ReviewEndpointUtterancesUIStore;
    onChangePredictedIntent: (id: LuisId, intentId: LuisId) => void;
    onLoadMore: () => void;
    onSubmit: (id: LuisId) => void;
    onDiscard: (id: LuisId) => void;
    onConfirmPredictions: (id: LuisId) => void;

    /**
     * User role to determine the actions.
     */
    userRole: string;
};

const messages = defineMessages({
    utterance: {
        id: 'utterance.suggestUtterancesTable.utterance',
        defaultMessage: 'Utterance'
    },
    predictedIntent: {
        id: 'utterance.suggestUtterancesTable.predictedIntent',
        defaultMessage: 'Predicted Intent'
    },
    addOrDelete: {
        id: 'utterance.suggestUtterancesTable.addOrDelete',
        defaultMessage: 'Add/Delete'
    },
    loadMore: {
        id: 'utterance.suggestUtterancesTable.loadMore',
        defaultMessage: 'Load More'
    },
    loadingSuggestions: {
        id: 'utterance.suggestUtterancesTable.loadingSuggestions',
        defaultMessage: 'Loading suggestions ...'
    },
    emptyTablePlaceholder: {
        id: 'utterance.suggestUtterancesTable.emptyTablePlaceholder',
        defaultMessage: 'No suggested utterances for selected model.'
    },
    choosePredictedIntent: {
        id: 'utterance.suggestUtterancesTable.choosePredictedIntent',
        defaultMessage: 'Choose predicted intent ...'
    }
});

type PredictedIntentSelectorProps = {
    computedUtterance: SuggestedUtteranceObservable;
    inFlightUtteranceIds: Set<LuisId>;
    onChangePredictedIntent: (id: LuisId, intentId: LuisId) => void;
};

const PredictedIntentSelector = observer((props: PredictedIntentSelectorProps) => {
    const { computedUtterance, onChangePredictedIntent, inFlightUtteranceIds } = props;
    const { localize } = useLocalization();
    const [utterance] = computedUtterance.computed.get();

    const intentsOptions: IDropdownOption[] = utterance.predictedIntents.map(predictedIntent => ({
        key: predictedIntent.intent.name,
        text: `${predictedIntent.intent.name} (${predictedIntent.score})`
    }));

    const disabled = inFlightUtteranceIds.has(utterance.id);

    return (
        <ComboBox
            disabled={disabled}
            scrollSelectedToTop
            useComboBoxAsMenuWidth
            styles={comboBoxStyles}
            options={intentsOptions}
            selectedKey={utterance.labeledIntent.name}
            ariaLabel={localize(messages.choosePredictedIntent)}
            placeholder={localize(messages.choosePredictedIntent)}
            onChange={(_, item) => {
                onChangePredictedIntent(
                    utterance.id,
                    utterance.predictedIntents.find(({ intent }) => intent.name === item.key)?.intent?.id
                );
            }}
        />
    );
});

/**
 * The table displaying some suggested utterances -based on the user input- to add to the application.
 */
const SuggestUtterancesTableBase = (props: SuggestUtterancesTableProps) => {
    const {
        computedSuggestedUtterances,
        reviewEndpointUtteranceUIStore,
        isLoading,
        isRTL,
        onChangePredictedIntent,
        onLoadMore,
        onConfirmPredictions,
        onDiscard,
        onSubmit,
        userRole,
        ...rest
    } = props;
    const { localize } = useLocalization();
    const triggerResize = useResizeTrigger();
    const debouncedTriggerSize = React.useCallback(debounce(triggerResize, 400, { leading: true }), [triggerResize]);

    const loadMoreDisplay = !isLoading && computedSuggestedUtterances.length === 10;

    const columns: IColumn[] = [
        {
            key: 'utterance',
            name: localize(messages.utterance),
            fieldName: 'utterance',
            minWidth: 100,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            onColumnResize: debouncedTriggerSize,
            onRender: (computedUtterance: SuggestedUtteranceObservable) => (
                <SuggestedUtteranceRow
                    key={computedUtterance.id}
                    utteranceObservable={computedUtterance}
                    isRTL={isRTL}
                    reviewEndpointUtteranceUIStore={reviewEndpointUtteranceUIStore}
                    onConfirmPredictions={onConfirmPredictions}
                    onDiscard={onDiscard}
                    onSubmit={onSubmit}
                    userRole={userRole}
                />
            )
        },
        {
            key: 'predictedIntent',
            name: localize(messages.predictedIntent),
            fieldName: 'predictedIntent',
            minWidth: 200,
            maxWidth: 700,
            isResizable: true,
            isCollapsible: false,
            onRender: (computedUtterance: SuggestedUtteranceObservable) => (
                <PredictedIntentSelector
                    computedUtterance={computedUtterance}
                    inFlightUtteranceIds={reviewEndpointUtteranceUIStore.inFlightUtteranceIds}
                    onChangePredictedIntent={onChangePredictedIntent}
                />
            )
        }
    ];

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <Table
                emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
                items={computedSuggestedUtterances}
                columns={columns}
                isLoading={isLoading}
                {...rest}
            />
            {loadMoreDisplay && (
                <Stack horizontalAlign="center">
                    <PrimaryButton text={localize(messages.loadMore)} onClick={onLoadMore} />
                </Stack>
            )}
        </Stack>
    );
};

/**
 * Wraps the suggest utterance table with ResizeProvider that synchronizes resizing utterance
 * table column with the Labeling LayoutManager.
 */
export const SuggestUtterancesTable = (props: SuggestUtterancesTableProps) => (
    <ResizeProvider>
        <SuggestUtterancesTableBase {...props} />
    </ResizeProvider>
);
